import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { LogOut, User, Briefcase, Layers, Settings, MessageCircle, HelpCircle, BarChart, CreditCard, FileText, Users, Code2 } from 'lucide-react';import { supabase } from '../../lib/common/supabase';
import { TypedLogo } from '../../components/common/TypedLogo'
import { NavbarSearch } from '../../components/common/NavbarSearch';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { useRecruiterPermission } from '../../hooks/useRecruiterPermission';
import { BetaTestModal } from '../../components/jobseeker/BetaTestModal'



export function DashboardNavigation() {
  const navigate = useNavigate();
  const location = useLocation();
  const isOnboardingPage = location.pathname === '/dashboard/complete-employer-profile';
  const shouldHideNavigation = location.pathname === '/dashboard/select-role' || location.pathname === '/dashboard/associate-company';
  const [scrolled, _setScrolled] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { profile, isLoadingInitial: isAuthLoading } = useAuth();
  const { hasPermission: canViewAnalytics } = useRecruiterPermission('view_analytics');
  const [currentFlipWordIndex, setCurrentFlipWordIndex] = useState(0);
  const employerFlipWords = ["titles", "expertise", "skills"];
  const jobSeekerFlipWords = ["jobs", "companies", "challenges"];
  const [isBetaTestModalOpen, setIsBetaTestModalOpen] = useState(false);

  useEffect(() => {
    const words = profile?.role === 'employer' ? employerFlipWords : jobSeekerFlipWords;
    const interval = setInterval(() => {
      setCurrentFlipWordIndex((prev) => (prev + 1) % words.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [profile?.role]);

  const currentFlipWord = profile ?
    (profile.role === 'employer' ? employerFlipWords : jobSeekerFlipWords)[currentFlipWordIndex] :
    undefined;

  useEffect(() => {
    setIsNavigating(true);
    const timer = setTimeout(() => {
      setIsNavigating(false);
    }, 300);

    return () => clearTimeout(timer);
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownRef]);

  const getInitials = (name: string | null | undefined): string => {
    if (!name) return '';
    const names = name.split(' ');
    const initials = names.map(n => n[0]).join('').toUpperCase();
    return initials.length > 2 ? initials.substring(0, 2) : initials;
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(prev => !prev);
  };


  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/signin');
  };
  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(path + '/');

  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const [unseenCandidatesCount, setUnseenCandidatesCount] = useState(0);
  const [unseenSharedJobsCount, setUnseenSharedJobsCount] = useState(0);

  const fetchUnreadSenderCount = useCallback(async () => {
    if (!profile || isAuthLoading || location.pathname === '/dashboard/messages') {
      setUnreadMessageCount(0);
      return;
    }

    try {
      const { data: conversationsWithUnread, error } = await supabase
        .from('conversation_participants')
        .select(`
        conversation_id,
        last_read_at,
        conversations!inner(
          id,
          last_message_at
        )
      `)
        .eq('user_id', profile.id)
        .or('is_hidden.is.null,is_hidden.eq.false');

      if (error || !conversationsWithUnread) {

        setUnreadMessageCount(0);
        return;
      }
      const unreadConversations = conversationsWithUnread.filter(cp => {
        const conv = cp.conversations as any;
        const hasUnread = conv && conv.last_message_at &&
          (!cp.last_read_at || new Date(conv.last_message_at) > new Date(cp.last_read_at));
        return hasUnread;
      });
      if (unreadConversations.length > 0) {
        const convoIds = unreadConversations.map(c => c.conversation_id);

        const { data: messages, error: messagesError } = await supabase
          .from('messages')
          .select('sender_id, conversation_id, created_at')
          .in('conversation_id', convoIds)
          .neq('sender_id', profile.id);

        if (!messagesError && messages) {
          const unreadMessages = messages.filter(msg => {
            const conv = unreadConversations.find(c => c.conversation_id === msg.conversation_id);
            return conv && (!conv.last_read_at || new Date(msg.created_at) > new Date(conv.last_read_at));
          });
          const uniqueSenders = new Set(unreadMessages.map(m => m.sender_id));

          setUnreadMessageCount(uniqueSenders.size);
        } else {
          setUnreadMessageCount(0);
        }
      } else {
        setUnreadMessageCount(0);
      }

    } catch (err) {

      setUnreadMessageCount(0);
    }
  }, [profile, location.pathname, isAuthLoading]);

  const fetchUnseenCandidatesCount = useCallback(async () => {
    if (!profile || isAuthLoading || profile.role !== 'employer' || location.pathname === '/dashboard/interested-candidates') {
      setUnseenCandidatesCount(0);
      return;
    }

    try {
      const lastViewedAt = localStorage.getItem('interested_candidates_last_viewed_at');

      const { data: userJobOpenings, error: jobsError } = await supabase
        .from('job_openings')
        .select('id')
        .eq('recruiter_profile_id', profile.id);

      if (jobsError || !userJobOpenings || userJobOpenings.length === 0) {
        setUnseenCandidatesCount(0);
        return;
      }

      const userJobIds = userJobOpenings.map(job => job.id);

      const { data, error } = await supabase
        .rpc('get_interested_candidates_for_employer', {
          p_employer_id: profile.id
        });

      if (error || !data) {

        setUnseenCandidatesCount(0);
        return;
      }

      const filteredCandidates = data.filter((candidate: any) =>
        userJobIds.includes(candidate.job_opening_id)
      );

      let unseenCount = 0;
      if (!lastViewedAt) {
        unseenCount = filteredCandidates.length;
      } else {
        const lastViewedTimestamp = new Date(lastViewedAt);
        unseenCount = filteredCandidates.filter((candidate: any) =>
          new Date(candidate.expressed_interest_at) > lastViewedTimestamp
        ).length;
      }


      setUnseenCandidatesCount(unseenCount);
    } catch (err) {

      setUnseenCandidatesCount(0);
    }
  }, [profile, location.pathname, isAuthLoading]);

  const fetchUnseenSharedJobsCount = useCallback(async () => {
    if (!profile || isAuthLoading || profile.role !== 'employer' || location.pathname === '/dashboard/interested-candidates') {
      setUnseenSharedJobsCount(0);
      return;
    }

    try {
      const lastViewedAt = localStorage.getItem('shared_jobs_last_viewed_at');

      const { data: sharedJobs, error } = await supabase
        .from('job_sharing')
        .select('created_at')
        .eq('shared_with', profile.id);

      if (error || !sharedJobs) {
        setUnseenSharedJobsCount(0);
        return;
      }

      let unseenCount = 0;
      if (!lastViewedAt) {
        unseenCount = sharedJobs.length;
      } else {
        const lastViewedTimestamp = new Date(lastViewedAt);
        unseenCount = sharedJobs.filter((share: any) =>
          new Date(share.created_at) > lastViewedTimestamp
        ).length;
      }

      setUnseenSharedJobsCount(unseenCount);
    } catch (err) {
      setUnseenSharedJobsCount(0);
    }
  }, [profile, location.pathname, isAuthLoading]);

  useEffect(() => {


    const handleMessagesOpened = () => {

      setUnreadMessageCount(0);
    };

    const handleCandidatesViewed = () => {
      setUnseenCandidatesCount(0);
    };

    const handleSharedJobsViewed = () => {
      setUnseenSharedJobsCount(0);
    };

    window.addEventListener('messagesOpened', handleMessagesOpened);
    window.addEventListener('candidatesViewed', handleCandidatesViewed);
    window.addEventListener('sharedJobsViewed', handleSharedJobsViewed);


    if (!isAuthLoading) {
      fetchUnreadSenderCount();
      if (profile?.role === 'employer') {
        fetchUnseenCandidatesCount();
        fetchUnseenSharedJobsCount();
      }
    }

    const channel = supabase
      .channel('dashboard-nav-msgs')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages' }, (_payload) => {
        fetchUnreadSenderCount();
      })
      .subscribe();

    const candidatesChannel = profile?.role === 'employer' ? supabase
      .channel('dashboard-nav-candidates')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'job_interactions' }, (payload) => {
        if (payload.new && payload.new.status === 'interested') {
          fetchUnseenCandidatesCount();
        }
      })
      .subscribe() : null;

    const sharedJobsChannel = profile?.role === 'employer' ? supabase
      .channel('dashboard-nav-shared-jobs')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'job_sharing',
        filter: `shared_with=eq.${profile?.id}`
      }, () => {
        fetchUnseenSharedJobsCount();
      })
      .subscribe() : null;

    return () => {
      window.removeEventListener('messagesOpened', handleMessagesOpened);
      window.removeEventListener('candidatesViewed', handleCandidatesViewed);
      window.removeEventListener('sharedJobsViewed', handleSharedJobsViewed);
      supabase.removeChannel(channel);
      if (candidatesChannel) {
        supabase.removeChannel(candidatesChannel);
      }
      if (sharedJobsChannel) {
        supabase.removeChannel(sharedJobsChannel);
      }
    };
  }, [fetchUnreadSenderCount, fetchUnseenCandidatesCount, fetchUnseenSharedJobsCount, isAuthLoading, profile?.role, profile?.id]);

  const contextUserRole = profile?.role;
  const contextAvatarUrl = profile?.avatar_url;
  const contextUserFullName = profile?.full_name;

  if (shouldHideNavigation) {
    return null;
  }



  return (
    <nav className="fixed w-full z-[100] flex justify-center pt-5 px-4">
      <motion.div
        animate={{
          opacity: isNavigating ? 0.3 : 1,
        }}
        transition={{
          duration: (location.pathname === '/dashboard/employer-analytics' ||
            location.pathname === '/dashboard/search-results' ||
            location.pathname.startsWith('/dashboard/public-user-profile')) ? 2 : 0.3,
          ease: "easeInOut"
        }}
        className={`relative bg-zinc-900/80 rounded-2xl border ${scrolled ? 'border-neutral-700' : 'border-neutral-800'
          } px-4 sm:px-6 md:px-8 w-full max-w-[1280px]`}
      >
        <div className="flex h-12 items-center">
          <div className="flex items-center flex-shrink-0">
  <Link to={contextUserRole === 'job_seeker' ? "/dashboard/career-intelligence" : "/dashboard/profile"} className="cursor-pointer">
    {useMemo(() => <TypedLogo />, [])}
  </Link>
</div>
          {!isOnboardingPage && (
            <div className="hidden lg:flex flex-grow justify-center px-4 mx-4 max-w-xl">
              <NavbarSearch
                disabled={profile?.role === 'employer' && profile?.company_approval_status === 'pending'}
                currentFlipWord={currentFlipWord}
              />
            </div>
          )}
          <div className="flex-grow"></div>

          <div className="flex items-center space-x-3 md:space-x-4 lg:space-x-6 flex-shrink-0">
            {!isOnboardingPage && !isAuthLoading && (
              <>
                {contextUserRole === 'job_seeker' && (
                  <>
                   

<Link
  to="/dashboard/browse-jobs"
  className={`${isActive('/dashboard/browse-jobs')
    ? 'text-white'
    : 'text-neutral-400 hover:text-white'} transition-colors text-sm font-medium relative group flex items-center space-x-1.5 md:space-x-2`}
>
  <Layers className="h-4 w-4" />
  <span className="hidden sm:inline">Browse Jobs</span>
  <span className="sm:hidden">Jobs</span>
</Link>

                    <Link
                      to="/dashboard/challenges"
                      className={`${isActive('/dashboard/challenges')
                        ? 'text-white'
                        : 'text-neutral-400 hover:text-white'} transition-colors text-sm font-medium relative group flex items-center space-x-1.5 md:space-x-2`}
                    >
                      <Briefcase className="h-4 w-4" />
                      <span className="hidden sm:inline">Challenges</span>
                    </Link>

                  </>
                )}
                {contextUserRole === 'employer' && (
                  <>
                    <Link
                      to="/dashboard/employer-challenges"
                      className={`${isActive('/dashboard/employer-challenges')
                        ? 'text-white'
                        : 'text-neutral-400 hover:text-white'} transition-colors text-sm font-medium relative group flex items-center space-x-1.5 md:space-x-2`}
                    >
                      <Code2 className="h-4 w-4" />
                      <span className="hidden sm:inline">Challenges</span>
                    </Link>
                    <Link
                      to="/dashboard/interested-candidates"
                      className={`${isActive('/dashboard/interested-candidates')
                        ? 'text-white'
                        : 'text-neutral-400 hover:text-white'} transition-colors text-sm font-medium relative group flex items-center space-x-1.5 md:space-x-2`}
                    >
                      <Users className="h-4 w-4" />
                      <span className="hidden sm:inline">Candidates</span>
                      {(unseenCandidatesCount > 0 || unseenSharedJobsCount > 0) && (
                        <span className="absolute -top-1.5 -right-3.5 sm:-right-3.5 flex h-4 w-4 items-center justify-center rounded-full bg-neutral-500 text-xs font-bold text-white" style={{ fontSize: '0.6rem', lineHeight: '1rem' }}>
                          {(unseenCandidatesCount + unseenSharedJobsCount) > 9 ? '9+' : (unseenCandidatesCount + unseenSharedJobsCount)}
                        </span>
                      )}
                    </Link>
                  </>
                )}
                <Link
                  to="/dashboard/messages"
                  className={`${isActive('/dashboard/messages')
                    ? 'text-white'
                    : 'text-neutral-400 hover:text-white'} transition-colors text-sm font-medium relative group flex items-center space-x-1.5 md:space-x-2`}
                >
                  <MessageCircle className="h-4 w-4" />
                  <span className="hidden sm:inline">Messages</span>
                  {unreadMessageCount > 0 && (
                    <span className="absolute -top-1.5 -right-3.5 sm:-right-3.5 flex h-4 w-4 items-center justify-center rounded-full bg-neutral-500 text-xs font-bold text-white" style={{ fontSize: '0.6rem', lineHeight: '1rem' }}>
                      {unreadMessageCount > 9 ? '9+' : unreadMessageCount}
                    </span>
                  )}
                </Link>
                <Link
                  to="/dashboard/profile"
                  className={`${isActive('/dashboard/profile')
                    ? 'text-white'
                    : 'text-neutral-400 hover:text-white'} transition-colors text-sm font-medium relative group flex items-center space-x-1.5 md:space-x-2`}
                >
                  <User className="h-4 w-4" />
                  <span className="hidden sm:inline">Profile</span>
                </Link>
              </>
            )}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={toggleDropdown}
                className="flex items-center justify-center w-8 h-8 rounded-full overflow-hidden bg-white/5 hover:bg-white/10 transition-all"
                aria-expanded={isDropdownOpen}
                aria-haspopup="true"
                aria-label="User menu"
              >
                {contextAvatarUrl ? (
                  <img src={contextAvatarUrl} alt="User Avatar" className="w-full h-full object-cover" />
                ) : contextUserFullName ? (
                  <span className="font-semibold text-xs text-white/60 select-none">
                    {getInitials(contextUserFullName)}
                  </span>
                ) : (
                  <User className="h-5 w-5 text-white/40" />
                )}
              </button>

              {isDropdownOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -10, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -10, scale: 0.95, transition: { duration: 0.15 } }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                  className="absolute right-0 mt-2 w-64 origin-top-right rounded-xl bg-[#1E1E20] text-white shadow-xl ring-1 ring-black/20 border border-neutral-700 z-50"
                  role="menu"
                  aria-orientation="vertical"
                  aria-labelledby="user-menu-button"
                >
                  <div className="px-4 py-3 border-b border-neutral-700">
                    <p className="text-sm font-medium truncate text-white">{contextUserFullName || "User"}</p>
                  </div>
                  <div className="py-1 flex flex-col gap-1 text-sm text-neutral-300">
                    {(contextUserRole === 'job_seeker' || profile?.company_role === 'admin' || (profile?.company_role === 'recruiter' && canViewAnalytics)) && (
                      <Link
                        to={contextUserRole === 'employer' ? "/dashboard/employer-analytics" : "/dashboard/analytics"}
                        onClick={() => setIsDropdownOpen(false)}
                        className="flex items-center px-4 py-2 hover:bg-[#2A2A2E] hover:text-white transition-colors"
                        role="menuitem"
                      >
                        <BarChart className="h-4 w-4 mr-3 text-neutral-400" />
                        Analytics
                      </Link>
                    )}
                    {profile?.company_role !== 'recruiter' && (
                      <Link
                        to={contextUserRole === 'employer' ? "/dashboard/employer-subscription-tiers" : "/dashboard/subscription-tiers"}
                        onClick={() => setIsDropdownOpen(false)}
                        className="flex items-center px-4 py-2 hover:bg-[#2A2A2E] hover:text-white transition-colors"
                        role="menuitem"
                      >
                        <CreditCard className="h-4 w-4 mr-3 text-neutral-400" />
                        Subscription Tiers
                      </Link>
                    )}
                    <Link
                      to="/dashboard/settings"
                      onClick={() => setIsDropdownOpen(false)}
                      className="flex items-center px-4 py-2 hover:bg-[#2A2A2E] hover:text-white transition-colors"
                      role="menuitem"
                    >
                      <Settings className="h-4 w-4 mr-3 text-neutral-400" />
                      Settings
                    </Link>
                    <Link
                      to="/dashboard/why-techfolio"
                      onClick={() => setIsDropdownOpen(false)}
                      className="flex items-center px-4 py-2 hover:bg-[#2A2A2E] hover:text-white transition-colors"
                      role="menuitem"
                    >
                      <HelpCircle className="h-4 w-4 mr-3 text-neutral-400" />
                      Why Techfolio?
                    </Link>
                    <Link
                      to="/terms-and-conditions"
                      onClick={() => setIsDropdownOpen(false)}
                      className="flex items-center px-4 py-2 hover:bg-[#2A2A2E] hover:text-white transition-colors"
                      role="menuitem"
                    >
                      <FileText className="h-4 w-4 mr-3 text-neutral-400" />
                      Terms & Conditions
                    </Link>
                    <Link
                      to="/privacy-policy"
                      onClick={() => setIsDropdownOpen(false)}
                      className="flex items-center px-4 py-2 hover:bg-[#2A2A2E] hover:text-white transition-colors"
                      role="menuitem"
                    >
                      <FileText className="h-4 w-4 mr-3 text-neutral-400" />
                      Privacy Policy
                    </Link>
                    <button
                      onClick={() => {
                        setIsBetaTestModalOpen(true);
                        setIsDropdownOpen(false);
                      }}
                      className="w-full flex items-center px-4 py-2 text-sm text-neutral-300 hover:bg-[#2A2A2E] hover:text-white transition-colors"
                      role="menuitem"
                    >
                      <MessageCircle className="h-4 w-4 mr-3 text-neutral-400" />
                      Beta Test
                    </button>
                  </div>
                  <div className="border-t border-neutral-700 my-1"></div>
                  <button
                    onClick={() => {
                      handleSignOut();
                      setIsDropdownOpen(false);
                    }}
                    className="w-full flex items-center px-4 py-2 text-sm text-neutral-300 hover:bg-[#2A2A2E] hover:text-white transition-colors"
                    role="menuitem"
                  >
                    <LogOut className="h-4 w-4 mr-3 text-neutral-400" />
                    Log out
                  </button>



                </motion.div>
              )}
            </div>
          </div>
        </div>
      </motion.div>
      
      <BetaTestModal
        isOpen={isBetaTestModalOpen}
        onClose={() => setIsBetaTestModalOpen(false)}
        userId={profile?.id || ''}
        userName={profile?.full_name || ''}
        userRole={profile?.role || 'job_seeker'}
      />
    </nav>
  );
}